/**
 * ⚙️ SPACE SETTINGS - Module Paramètres & Sécurité
 * Gestion des paramètres de sécurité de l'application incluant le 2FA
 */

import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Settings, Shield, Lock, Smartphone, QrCode,
  ToggleLeft, ToggleRight, Loader2, RefreshCw,
  Info, CheckCircle, X, Eye, EyeOff, AlertTriangle
} from 'lucide-react';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { api } from '../../services/api';
import { toast } from 'sonner';

// Carte de paramètre réutilisable
const SettingCard = ({ 
  icon: Icon, 
  title, 
  description, 
  enabled, 
  onToggle, 
  isLoading,
  color = 'cyan',
  testId,
  children
}) => {
  const colors = {
    cyan: {
      border: 'border-cyan-500/30',
      bg: 'bg-cyan-500/10',
      iconBg: 'bg-cyan-500/20',
      iconColor: 'text-cyan-400',
      switch: 'data-[state=checked]:bg-cyan-500'
    },
    orange: {
      border: 'border-orange-500/30',
      bg: 'bg-orange-500/10',
      iconBg: 'bg-orange-500/20',
      iconColor: 'text-orange-400',
      switch: 'data-[state=checked]:bg-orange-500'
    },
    purple: {
      border: 'border-purple-500/30',
      bg: 'bg-purple-500/10',
      iconBg: 'bg-purple-500/20',
      iconColor: 'text-purple-400',
      switch: 'data-[state=checked]:bg-purple-500'
    },
    green: {
      border: 'border-green-500/30',
      bg: 'bg-green-500/10',
      iconBg: 'bg-green-500/20',
      iconColor: 'text-green-400',
      switch: 'data-[state=checked]:bg-green-500'
    }
  };

  const c = colors[color] || colors.cyan;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`backdrop-blur-xl rounded-2xl p-6 border ${c.border} ${c.bg}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className={`p-3 rounded-xl ${c.iconBg}`}>
            <Icon className={`w-6 h-6 ${c.iconColor}`} />
          </div>
          <div>
            <h3 className="text-white font-bold text-lg">{title}</h3>
            <p className="text-gray-400 text-sm mt-1">{description}</p>
          </div>
        </div>
        
        {onToggle && (
          <div className="flex items-center gap-3">
            {isLoading ? (
              <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
            ) : (
              <>
                <span className={`text-sm ${enabled ? 'text-green-400' : 'text-gray-500'}`}>
                  {enabled ? 'Activé' : 'Désactivé'}
                </span>
                <Switch
                  checked={enabled}
                  onCheckedChange={onToggle}
                  className={c.switch}
                  data-testid={testId}
                />
              </>
            )}
          </div>
        )}
      </div>
      {children && <div className="mt-4">{children}</div>}
    </motion.div>
  );
};

// Info box
const InfoBox = ({ children, variant = 'info' }) => {
  const variants = {
    info: 'border-blue-500/30 bg-blue-500/10 text-blue-400',
    warning: 'border-yellow-500/30 bg-yellow-500/10 text-yellow-400',
    success: 'border-green-500/30 bg-green-500/10 text-green-400'
  };

  return (
    <div className={`rounded-xl p-4 border ${variants[variant]} flex items-start gap-3`}>
      <Info className="w-5 h-5 mt-0.5 flex-shrink-0" />
      <p className="text-sm">{children}</p>
    </div>
  );
};

// Composant 2FA Setup avec QR Code
const TwoFactorSetup = ({ adminCode, onComplete, onCancel }) => {
  const [step, setStep] = useState('loading'); // loading, qrcode, verify, success
  const [qrCodeUrl, setQrCodeUrl] = useState(null);
  const [secretKey, setSecretKey] = useState(null);
  const [showSecret, setShowSecret] = useState(false);
  const [verifyCode, setVerifyCode] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const inputRefs = useRef([]);

  // Initialiser le setup 2FA
  useEffect(() => {
    const initSetup = async () => {
      try {
        const result = await api.setupAdmin2FA(adminCode);
        setQrCodeUrl(result.qrCode);
        setSecretKey(result.secret);
        setStep('qrcode');
      } catch (err) {
        toast.error('Erreur lors de l\'initialisation du 2FA');
        onCancel();
      }
    };
    initSetup();
  }, [adminCode, onCancel]);

  // Gérer la saisie du code
  const handleCodeChange = (index, value) => {
    if (!/^[0-9]*$/.test(value)) return;
    if (value.length > 1) value = value.slice(-1);
    
    const newCode = [...verifyCode];
    newCode[index] = value;
    setVerifyCode(newCode);
    setError('');

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit
    if (newCode.every(d => d !== '') && index === 5) {
      handleVerify(newCode.join(''));
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !verifyCode[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerify = async (code) => {
    setIsLoading(true);
    setError('');
    try {
      await api.verifyAdmin2FASetup(adminCode, code);
      setStep('success');
      toast.success('🔐 2FA Admin activé avec succès !');
      setTimeout(() => onComplete(), 2000);
    } catch (err) {
      setError('Code incorrect ou expiré. Réessayez.');
      setVerifyCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setIsLoading(false);
    }
  };

  if (step === 'loading') {
    return (
      <div className="flex flex-col items-center justify-center py-8">
        <Loader2 className="w-10 h-10 text-purple-400 animate-spin mb-4" />
        <p className="text-gray-400">Génération du QR code...</p>
      </div>
    );
  }

  if (step === 'success') {
    return (
      <motion.div 
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="flex flex-col items-center justify-center py-8"
      >
        <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mb-4">
          <CheckCircle className="w-10 h-10 text-green-400" />
        </div>
        <h3 className="text-xl font-bold text-white mb-2">2FA Activé !</h3>
        <p className="text-gray-400 text-center">
          La prochaine connexion admin nécessitera le code de Google Authenticator.
        </p>
      </motion.div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Étape 1: QR Code */}
      {step === 'qrcode' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          <div className="text-center">
            <h3 className="text-xl font-bold text-white mb-2">📱 Scannez ce QR Code</h3>
            <p className="text-gray-400 text-sm">
              Utilisez Google Authenticator ou une app compatible
            </p>
          </div>

          {/* QR Code */}
          <div className="flex justify-center">
            <div className="p-4 bg-white rounded-2xl shadow-lg">
              {qrCodeUrl && (
                <img src={qrCodeUrl} alt="QR Code 2FA" className="w-48 h-48" />
              )}
            </div>
          </div>

          {/* Secret Key (backup) */}
          <div className="bg-black/30 rounded-xl p-4 border border-gray-700">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-400">Clé secrète (backup)</span>
              <button
                onClick={() => setShowSecret(!showSecret)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <code className="text-sm text-purple-400 font-mono break-all">
              {showSecret ? secretKey : '••••••••••••••••••••••••••••••••'}
            </code>
            <p className="text-xs text-gray-500 mt-2">
              Conservez cette clé en lieu sûr. Elle vous permettra de récupérer l&apos;accès si vous perdez votre téléphone.
            </p>
          </div>

          <Button
            onClick={() => {
              setStep('verify');
              setTimeout(() => inputRefs.current[0]?.focus(), 100);
            }}
            className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
          >
            J&apos;ai scanné le QR Code →
          </Button>
        </motion.div>
      )}

      {/* Étape 2: Vérification */}
      {step === 'verify' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          <div className="text-center">
            <h3 className="text-xl font-bold text-white mb-2">🔢 Entrez le code</h3>
            <p className="text-gray-400 text-sm">
              Saisissez le code à 6 chiffres de votre application
            </p>
          </div>

          {/* Code Input */}
          <div className="flex justify-center gap-2">
            {verifyCode.map((digit, index) => (
              <input
                key={index}
                ref={el => inputRefs.current[index] = el}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleCodeChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                className={`w-12 h-14 text-center text-2xl font-bold rounded-xl border-2 
                  ${error ? 'border-red-500 bg-red-500/10' : 'border-purple-500/30 bg-black/30'} 
                  text-white focus:border-purple-500 focus:outline-none transition-colors`}
                disabled={isLoading}
              />
            ))}
          </div>

          {error && (
            <p className="text-red-400 text-sm text-center">{error}</p>
          )}

          <div className="flex gap-3">
            <Button
              onClick={() => setStep('qrcode')}
              variant="outline"
              className="flex-1 border-gray-600"
              disabled={isLoading}
            >
              ← Retour
            </Button>
            <Button
              onClick={() => handleVerify(verifyCode.join(''))}
              className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500"
              disabled={isLoading || verifyCode.some(d => d === '')}
            >
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Activer le 2FA'}
            </Button>
          </div>
        </motion.div>
      )}

      {/* Bouton Annuler */}
      <button
        onClick={onCancel}
        className="w-full text-gray-500 hover:text-gray-300 text-sm transition-colors"
      >
        Annuler
      </button>
    </div>
  );
};

// Composant pour désactiver le 2FA
const TwoFactorDisable = ({ adminCode, onComplete, onCancel }) => {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const inputRefs = useRef([]);

  const handleCodeChange = (index, value) => {
    if (!/^[0-9]*$/.test(value)) return;
    if (value.length > 1) value = value.slice(-1);
    
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setError('');

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleDisable = async () => {
    const totpCode = code.join('');
    if (totpCode.length !== 6) return;

    setIsLoading(true);
    setError('');
    try {
      await api.disableAdmin2FA(adminCode, totpCode);
      toast.success('⚠️ 2FA Admin désactivé');
      onComplete();
    } catch (err) {
      setError('Code incorrect. Réessayez.');
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
          <AlertTriangle className="w-8 h-8 text-red-400" />
        </div>
        <h3 className="text-xl font-bold text-white mb-2">Désactiver le 2FA</h3>
        <p className="text-gray-400 text-sm">
          Entrez votre code actuel pour confirmer
        </p>
      </div>

      {/* Code Input */}
      <div className="flex justify-center gap-2">
        {code.map((digit, index) => (
          <input
            key={index}
            ref={el => inputRefs.current[index] = el}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            onChange={(e) => handleCodeChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            className={`w-12 h-14 text-center text-2xl font-bold rounded-xl border-2 
              ${error ? 'border-red-500 bg-red-500/10' : 'border-red-500/30 bg-black/30'} 
              text-white focus:border-red-500 focus:outline-none transition-colors`}
            disabled={isLoading}
          />
        ))}
      </div>

      {error && (
        <p className="text-red-400 text-sm text-center">{error}</p>
      )}

      <div className="flex gap-3">
        <Button
          onClick={onCancel}
          variant="outline"
          className="flex-1 border-gray-600"
          disabled={isLoading}
        >
          Annuler
        </Button>
        <Button
          onClick={handleDisable}
          className="flex-1 bg-red-500 hover:bg-red-600"
          disabled={isLoading || code.some(d => d === '')}
        >
          {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Désactiver'}
        </Button>
      </div>
    </div>
  );
};

// Module Settings principal
const SpaceSettings = ({ adminCode }) => {
  const [pinEnabled, setPinEnabled] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  
  // 2FA States
  const [twoFAStatus, setTwoFAStatus] = useState({ enabled: false, configured: false });
  const [showSetup2FA, setShowSetup2FA] = useState(false);
  const [showDisable2FA, setShowDisable2FA] = useState(false);

  // Charger les paramètres
  const loadSettings = async () => {
    setIsLoading(true);
    try {
      const [settingsData, twoFAData] = await Promise.all([
        api.getSettings(),
        api.getAdmin2FAStatus()
      ]);
      setPinEnabled(settingsData.pinEnabled ?? true);
      setTwoFAStatus(twoFAData);
    } catch (error) {
      console.error('Erreur chargement paramètres:', error);
      toast.error('Erreur de chargement des paramètres');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  // Toggle PIN
  const handleTogglePin = async () => {
    setUpdating(true);
    try {
      const result = await api.updateSettings(!pinEnabled);
      setPinEnabled(result.pinEnabled);
      toast.success(
        result.pinEnabled 
          ? '🔐 Code PIN activé pour l\'espace Team' 
          : '🔓 Accès libre à l\'espace Team (sans PIN)'
      );
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setUpdating(false);
    }
  };

  // Handle 2FA setup complete
  const handle2FASetupComplete = () => {
    setShowSetup2FA(false);
    setTwoFAStatus({ enabled: true, configured: true });
  };

  // Handle 2FA disable complete
  const handle2FADisableComplete = () => {
    setShowDisable2FA(false);
    setTwoFAStatus({ enabled: false, configured: false });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">
            ⚙️ PARAMÈTRES & SÉCURITÉ
          </h1>
          <p className="text-gray-500 text-sm">Configuration de la sécurité du vaisseau</p>
        </div>
        <Button
          onClick={loadSettings}
          variant="outline"
          className="border-cyan-500/30 text-cyan-400"
          disabled={isLoading}
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
          Actualiser
        </Button>
      </div>

      {/* Section 2FA Admin */}
      <div className="space-y-4">
        <h2 className="text-lg font-bold text-white flex items-center gap-2">
          <Smartphone className="w-5 h-5 text-purple-400" />
          Authentification à Deux Facteurs (2FA)
        </h2>
        
        <AnimatePresence mode="wait">
          {showSetup2FA ? (
            <motion.div
              key="setup"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="backdrop-blur-xl rounded-2xl p-6 border border-purple-500/30 bg-purple-500/10"
            >
              <TwoFactorSetup
                adminCode={adminCode}
                onComplete={handle2FASetupComplete}
                onCancel={() => setShowSetup2FA(false)}
              />
            </motion.div>
          ) : showDisable2FA ? (
            <motion.div
              key="disable"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="backdrop-blur-xl rounded-2xl p-6 border border-red-500/30 bg-red-500/10"
            >
              <TwoFactorDisable
                adminCode={adminCode}
                onComplete={handle2FADisableComplete}
                onCancel={() => setShowDisable2FA(false)}
              />
            </motion.div>
          ) : (
            <motion.div
              key="status"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className={`backdrop-blur-xl rounded-2xl p-6 border ${
                twoFAStatus.enabled 
                  ? 'border-green-500/30 bg-green-500/10' 
                  : 'border-purple-500/30 bg-purple-500/10'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`p-3 rounded-xl ${
                    twoFAStatus.enabled ? 'bg-green-500/20' : 'bg-purple-500/20'
                  }`}>
                    {twoFAStatus.enabled ? (
                      <CheckCircle className="w-6 h-6 text-green-400" />
                    ) : (
                      <QrCode className="w-6 h-6 text-purple-400" />
                    )}
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-lg">
                      {twoFAStatus.enabled ? '2FA Activé' : '2FA Non configuré'}
                    </h3>
                    <p className="text-gray-400 text-sm mt-1">
                      {twoFAStatus.enabled 
                        ? 'Votre compte admin est protégé par Google Authenticator'
                        : 'Ajoutez une couche de sécurité supplémentaire à votre compte admin'
                      }
                    </p>
                  </div>
                </div>
                
                <Button
                  onClick={() => twoFAStatus.enabled ? setShowDisable2FA(true) : setShowSetup2FA(true)}
                  className={twoFAStatus.enabled 
                    ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/30'
                    : 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600'
                  }
                  data-testid="2fa-toggle-btn"
                >
                  {twoFAStatus.enabled ? 'Désactiver' : 'Configurer le 2FA'}
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <InfoBox variant={twoFAStatus.enabled ? 'success' : 'info'}>
          {twoFAStatus.enabled 
            ? 'La double authentification est active. Vous devrez entrer un code depuis Google Authenticator à chaque connexion admin.'
            : 'Le 2FA protège votre compte même si votre code PIN est compromis. Nous recommandons fortement de l\'activer.'
          }
        </InfoBox>
      </div>

      {/* Section Sécurité Espace Team */}
      <div className="space-y-4">
        <h2 className="text-lg font-bold text-white flex items-center gap-2">
          <Shield className="w-5 h-5 text-orange-400" />
          Sécurité Espace Team (Épiciers)
        </h2>
        
        <SettingCard
          icon={Lock}
          title="Code PIN Épiciers"
          description="Exiger un code PIN pour accéder au cockpit d'administration"
          enabled={pinEnabled}
          onToggle={handleTogglePin}
          isLoading={updating}
          color="orange"
          testId="pin-toggle"
        />

        <InfoBox>
          Quand activé, les épiciers (Momo, Jean-Phi, Abdel) doivent entrer leur code unique à 6 chiffres pour accéder au cockpit.
        </InfoBox>
      </div>

      {/* Note de sécurité générale */}
      <div className="pt-4">
        <div className="backdrop-blur-xl rounded-2xl p-5 border border-cyan-500/30 bg-cyan-500/5">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-cyan-500/20">
              <Shield className="w-5 h-5 text-cyan-400" />
            </div>
            <div>
              <h3 className="text-white font-bold mb-1">🛡️ Sécurité du vaisseau</h3>
              <ul className="text-gray-400 text-sm space-y-1">
                <li>✅ Rate-limiting actif (protection anti-bruteforce)</li>
                <li>✅ Connexions admin journalisées</li>
                <li>✅ Backups automatiques quotidiens</li>
                <li>{twoFAStatus.enabled ? '✅' : '⚠️'} 2FA Admin {twoFAStatus.enabled ? 'activé' : 'non configuré'}</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpaceSettings;
